import React, {useMemo, useState} from 'react';
import {
  StudioDatasetView,
  StudioDatasetViewer,
} from '../../base-components/StudioDataset/StudioDatasetViewer';
import {isEmpty} from 'lodash';
import {useStudioDatasetViewerContext} from '../../base-components/StudioDataset/StudioDatasetViewerProvider';
import {
  FilterProps,
  ItemType,
  ViewId,
} from '../../base-components/StudioDataset/FileViewer';
import {NO_GROUP} from '../../base-components/StudioDataset/FileViewer/LabelView';
import {RSQLProps} from '../../base-components/StudioDataset/DatasetLoader';
import {dataPrepareActions} from '../../store/dataPrepare';
import {RootState, useAppDispatch} from '../../store';
import {StudioContentLayout} from '../../base-components/StudioContentLayout';
import {useSelector} from 'react-redux';
import {
  getDataset,
  getDatasetCategoryMetadata,
  getDatasetMetadataSplits,
} from '../../store/dataService';
import {
  getFilterRSQL,
  FilterProps as OtherFilterProps,
} from '../DatasetEdit/DetectionStep/ImageListFilter';
import {VIEW_TYPE} from '../../base-components/StudioDataset/const';
import {matchPath, useLocation} from 'react-router';
import {PrivateRoutes} from '../../config/routes';
import {DataViewSummary, LabelCount} from './DataViewSummary';
import {Header} from './Header';
import {
  ThumbnailGridDetectionRenderer,
  ThumbnailGridClassificationRenderer,
} from './ThumbnailRenderer';
import URL from '../../config/url';
import {ImagePreviewModal} from './ImagePreview';
import './DatasetView.scss';
import {FIELDS_LIST} from '../../base-components/StudioDataset/FileViewer/DatasetLoader';
import {formatRawLabelCountArr} from '../../store/dataService/datasetMetaDataUtil';

// Custom directive from BE to fetch binaries with specific category
const RSQL_BINARY_JOIN = 'type==(binary/image)';
// Default Binary RSQL
const RSQL_BASE = {id: 'image', rsql: 'type=in=(binary/image)'};

export type DatasetViewProps = {
  datasetId?: string;
  datasetVersion?: number;
};

const getFullImageSrc = (binaryId?: string, datasetId?: string) => {
  if (binaryId && datasetId) {
    return `${URL.DSS_DATASET_BINARIES}?binaryId=${binaryId}&datasetId=${datasetId}&withContentDisposition=false`;
  }
  return '';
};

export const DatasetViewBase = ({datasetId, datasetVersion}: DatasetViewProps) => {
  const dispatch = useAppDispatch();
  const {setStatus} = dataPrepareActions;

  const [view, setView] = useState<ViewId>(VIEW_TYPE.LABEL);
  const datasetIsBusy = useSelector((state: RootState) => state.dataset.isBusy);
  const [initializedDataset, setInitializedDataset] = useState<boolean>(false);
  // Used for browsing sub category on Label View
  const [viewCategory, setViewCategory] = useState<string | null>(null);

  const [filter, setFilter] = useState<RSQLProps[] | undefined>();
  const [otherFilter, setOtherFilter] = useState<string | undefined>('split==TRAIN');
  const [search, setSearch] = useState<string | undefined>();

  const [previewItem, setPreviewItem] = useState<ItemType | undefined | null>();

  const {status} = useStudioDatasetViewerContext();

  const dataset = useSelector((state: RootState) => state.dataset.data);
  const metadata = useSelector((state: RootState) => state.datasetMetadata);
  const {categories: categoriesRaw} = metadata;
  const categories = categoriesRaw?.formattedCategories;
  const labeleSplits: LabelCount[] = formatRawLabelCountArr(
    metadata.splits?.labelTotals,
    categoriesRaw?.categories
  );

  const {pathname} = useLocation();
  const params = matchPath<{id: string}>(pathname, PrivateRoutes.DATASET_VIEW)?.params;
  const datasetIdParam = params?.id;

  // Load  Dataset
  useMemo(() => {
    if (
      !datasetIsBusy &&
      !initializedDataset &&
      datasetIdParam &&
      datasetIdParam !== dataset?.id
    ) {
      // Load Dataset
      dispatch(getDataset({datasetId: datasetIdParam})).then(res => {
        if (res.payload) {
          setInitializedDataset(true);
        } else {
          // TODO Implement confirm if need to create a new one or go to previous page
          // Need to verify with UX design if we want this behavior
          console.log('!!Handle missing dataset/error: ');
        }
      });
    }
  }, [datasetIdParam, dispatch, datasetIsBusy, initializedDataset, dataset]);

  // Load Dataset category metadata if missing
  useMemo(() => {
    if (dataset && !categoriesRaw) {
      dispatch(
        getDatasetCategoryMetadata({
          datasetId: dataset.id,
        })
      );
    }
  }, [dataset, dispatch, categoriesRaw]);

  useMemo(() => {
    const getVisibleCategorIds = () =>
      (search
        ? categories?.filter(cat => cat.name.toLowerCase().includes(search.toLowerCase()))
        : categories
      )?.map(cat => cat.id) || [];
    if (!isEmpty(categories)) {
      if (view === VIEW_TYPE.LABEL) {
        const newFilter: RSQLProps[] = [];
        if (viewCategory) {
          // View specific category
          newFilter.push(
            viewCategory === NO_GROUP
              ? // unlabelled
                {
                  id: NO_GROUP,
                  rsql: `${RSQL_BINARY_JOIN};category=ex=false${
                    otherFilter ? `;${otherFilter}` : ''
                  }`,
                }
              : // labelled category
                {
                  id: viewCategory,
                  rsql: `${RSQL_BINARY_JOIN};category==${viewCategory}${
                    otherFilter ? `;${otherFilter}` : ''
                  }`,
                }
          );
        } else {
          // Full Label View
          getVisibleCategorIds().forEach(id =>
            newFilter.push({
              id: id.toString(),
              rsql: `${RSQL_BINARY_JOIN};category==${id}${
                otherFilter ? `;${otherFilter}` : ''
              }`,
            })
          );
          // Add unlabelled binaries
          newFilter.push({
            id: NO_GROUP,
            rsql: `${RSQL_BINARY_JOIN};category=ex=false${
              otherFilter ? `;${otherFilter}` : ''
            }`,
          });
        }
        setFilter(newFilter);
      } else {
        if (isEmpty(search)) {
          // no search
          setFilter([
            {
              ...RSQL_BASE,
              rsql: RSQL_BASE.rsql.concat(otherFilter ? `;${otherFilter}` : ''),
            },
          ]);
        } else {
          setFilter([
            {
              id: 'image',
              rsql: `${RSQL_BINARY_JOIN};category==(${getVisibleCategorIds().join()})`.concat(
                otherFilter ? `;${otherFilter}` : ''
              ),
            },
          ]);
        }
      }
    }
  }, [view, viewCategory, search, categories, otherFilter]);

  // Bubble up viewer status to app level
  useMemo(() => dispatch(setStatus(status)), [status, setStatus, dispatch]);

  // Get Statistics
  useMemo(() => {
    if (dataset) {
      const type =
        dataset.purpose === 'CLASSIFICATION'
          ? // Classificaiton stats
            'statistics/labels_by_splits'
          : // Detection stats
            'statistics/bounding_boxes_by_splits';
      dispatch(
        getDatasetMetadataSplits({
          datasetId: dataset.id,
          version: dataset.version,
          type,
        })
      );
    }
  }, [dataset, dispatch]);

  const onViewerFilter = (localFilter: FilterProps | null, subView: boolean) => {
    const categoryId =
      localFilter?.label === 'Unlabeled'
        ? NO_GROUP
        : categories?.find(cat => cat.name === localFilter?.label)?.id.toString();
    setViewCategory(categoryId || null);
  };
  const onHeaderFilter = (args: OtherFilterProps) => setOtherFilter(getFilterRSQL(args));

  const onClick = (item: ItemType) => setPreviewItem(item);
  const onPreviewClose = () => setPreviewItem(null);

  return (
    <div className="dataset-view">
      <div className="dataset-view__body">
        <StudioContentLayout
          className="dataset-view__content"
          header={
            <div>
              <DataViewSummary
                name={dataset?.name}
                fileCount={dataset?.binaryTotal}
                sizeOfDataset={dataset?.size}
                numberOfClasses={categories?.length}
                date={dataset ? new Date(dataset.created).getTime() : undefined}
                filesByLabel={labeleSplits}
                onBarClick={setSearch}
                purposeOfDataset={dataset?.purpose}
              />
              <Header
                dataset={dataset}
                view={view}
                search={search}
                onView={setView}
                onSearch={setSearch}
                onFilter={onHeaderFilter}
              />
            </div>
          }
        >
          {({width, contentHeight}) => (
            <StudioDatasetViewer
              dataset={dataset}
              categories={categoriesRaw?.categories}
              width={width}
              height={contentHeight}
              showLabels={true}
              datasetVersion={datasetVersion}
              readOnly={true}
              view={view}
              filter={filter}
              labelHeader={categoriesRaw?.categories}
              onClick={onClick}
              itemRenderer={props => {
                return (dataset?.purpose === 'CLASSIFICATION'
                  ? ThumbnailGridClassificationRenderer
                  : ThumbnailGridDetectionRenderer)({
                  ...props,
                });
              }}
              onFilter={onViewerFilter}
              defaultFields={FIELDS_LIST}
              allowSelection={false}
            />
          )}
        </StudioContentLayout>
        <ImagePreviewModal
          onClick={onPreviewClose}
          onClose={onPreviewClose}
          filename={previewItem?.filename}
          src={getFullImageSrc(previewItem?.id, dataset?.id)}
          label={previewItem?.label}
          lastEdited={previewItem?.lastEdited.toString()}
          size={previewItem?.size || 0}
          width={previewItem?.width}
          height={previewItem?.height}
          open={!!previewItem}
          item={previewItem}
          showLabel={dataset?.purpose === 'DETECTION'}
        />
      </div>
    </div>
  );
};

export const DatasetView = (props: DatasetViewProps) => (
  <StudioDatasetView>
    <DatasetViewBase {...props} />
  </StudioDatasetView>
);
